/**
 * Constants transcribed from the scenario source.
 *
 *   maps/biter_battles_v2/tables.lua   — food values, difficulty ladder
 *   maps/biter_battles_v2/config.lua   — bb_config
 *   maps/biter_battles_v2/init.lua     — per-game defaults
 *   maps/biter_battles_v2/ai.lua       — threat values
 *
 * Two branches are transcribed, not one: the live scenario and the 3v3 balance
 * test. Anything that differs between them is suffixed or grouped — see
 * `rulesets.ts`, which is where the two sets are assembled into switchable
 * rulesets. Anything unsuffixed here is common to both.
 *
 * Nothing in `src/model` may import React or visx. Keep it a pure library.
 */

export type ScienceKey =
  | 'automation'
  | 'logistic'
  | 'military'
  | 'chemical'
  | 'production'
  | 'utility'
  | 'space';

export interface ScienceInfo {
  key: ScienceKey;
  /** Long name as used by the Factorio prototype / the Lua tables. */
  item: string;
  /** Human label used in game chat ("automation science"). */
  label: string;
  /** Colloquial colour name players actually use. */
  colour: string;
  /** `Tables.food_values[x].value` — raw mutagen per flask. */
  value: number;
  /** Hex colour for charts, derived from the in-game rgb string. */
  hex: string;
}

/** `Tables.food_values` — tables.lua:34 */
export const SCIENCE: Record<ScienceKey, ScienceInfo> = {
  automation: {
    key: 'automation',
    item: 'automation-science-pack',
    label: 'automation science',
    colour: 'red',
    value: 0.0011,
    hex: '#ff3232',
  },
  logistic: {
    key: 'logistic',
    item: 'logistic-science-pack',
    label: 'logistic science',
    colour: 'green',
    value: 0.0028,
    hex: '#32ff32',
  },
  military: {
    key: 'military',
    item: 'military-science-pack',
    label: 'military science',
    colour: 'grey',
    value: 0.0095,
    hex: '#9a9a9a',
  },
  chemical: {
    key: 'chemical',
    item: 'chemical-science-pack',
    label: 'chemical science',
    colour: 'blue',
    value: 0.0345,
    hex: '#64c8ff',
  },
  production: {
    key: 'production',
    item: 'production-science-pack',
    label: 'production science',
    colour: 'purple',
    value: 0.115,
    hex: '#9619ff',
  },
  utility: {
    key: 'utility',
    item: 'utility-science-pack',
    label: 'utility science',
    colour: 'yellow',
    value: 0.2205,
    hex: '#d2d23c',
  },
  space: {
    key: 'space',
    item: 'space-science-pack',
    label: 'space science',
    colour: 'white',
    value: 0.4375,
    hex: '#ffffff',
  },
};

export const SCIENCE_ORDER: ScienceKey[] = [
  'automation',
  'logistic',
  'military',
  'chemical',
  'production',
  'utility',
  'space',
];

/**
 * The game tooltip prints mutagen strength as `floor(value * 10000)`, so one
 * space flask reads "4375 Mutagen strength". Charts use those display units
 * because that is the number players actually see in game.
 */
export const MUTAGEN_DISPLAY_SCALE = 10000;

export interface Difficulty {
  index: number;
  name: string;
  shortName: string;
  /** `difficulty_vote_value` — the mutagen effectiveness multiplier. */
  value: number;
  /** True when `raise_evo` applies the long-game difficulty ramp (index 1..4). */
  ramps: boolean;
}

/** `Tables.difficulties` — tables.lua:127 */
export const DIFFICULTIES: Difficulty[] = [
  { index: 1, name: "I'm Too Young to Die", shortName: 'ITYTD', value: 0.2, ramps: true },
  { index: 2, name: 'Have a Nice Day', shortName: 'HaND', value: 0.35, ramps: true },
  { index: 3, name: 'Piece of Cake', shortName: 'PoC', value: 0.5, ramps: true },
  { index: 4, name: 'Easy', shortName: 'Easy', value: 0.75, ramps: true },
  { index: 5, name: 'Normal', shortName: 'Normal', value: 1, ramps: false },
  { index: 6, name: 'Hard', shortName: 'Hard', value: 2, ramps: false },
  { index: 7, name: 'Fun and Fast', shortName: 'FnF', value: 5, ramps: false },
];

/** `bb_config` — config.lua */
export const BB_CONFIG = {
  /** `threat_scale_factor_past_evo100` */
  threatScaleFactorPastEvo100: 3,
  /** `health_multiplier_boss` = 20 * 1.3, used by the threat refund on boss death. */
  healthMultiplierBoss: 20 * 1.3,
};

/** Per-game defaults set in init.lua. */
export const DEFAULTS = {
  /** `storage.max_reanim_thresh` — scale of the biter health curve. */
  maxReanimThresh: 250,
  /** `storage.max_group_size_initial` */
  maxGroupSizeInitial: 300,
  /** `storage.bb_threat_income = evo * 25`, applied once per second. */
  threatIncomePerEvoPerSecond: 25,
  /** `storage.difficulty_votes_timeout` in ticks. */
  difficultyVotesTimeoutTicks: 36000,
  /** Explorer default, not a game constant — a plausible small-server lobby. */
  playerCount: 6,
  /** Explorer default: index 5, "Normal", where one flask is worth its face value. */
  difficultyIndex: 5,
};

/**
 * `threat_values` — ai.lua:19. The price of a unit: threat is spent to spawn it
 * and the same value is taken back off the stock when it dies.
 */
export const THREAT_VALUES: Record<string, number> = {
  'small-spitter': 1.5,
  'small-biter': 1.5,
  'medium-spitter': 4.5,
  'medium-biter': 4.5,
  'big-spitter': 13,
  'big-biter': 13,
  'behemoth-spitter': 38.5,
  'behemoth-biter': 38.5,
  'small-worm-turret': 8,
  'medium-worm-turret': 16,
  'big-worm-turret': 24,
  'behemoth-worm-turret': 32,
  'biter-spawner': 32,
  'spitter-spawner': 32,
};

/* -------------------------------------------------------------------------- */
/*  cojito_3v3_balance_testing                                                 */
/* -------------------------------------------------------------------------- */

/**
 * `local small = 15` — ai.lua:19 on the test branch. Every price is written as
 * `n / small`, which puts one small biter at 1.0 and makes the whole threat
 * scale read as "small biters I can afford".
 */
export const TESTING_THREAT_UNIT_DIVISOR = 15;

/** `threat_values` — ai.lua:20 on the test branch, kept in its `n / small` form. */
export const TESTING_THREAT_VALUES: Record<string, number> = {
  'small-spitter': 10 / TESTING_THREAT_UNIT_DIVISOR,
  'small-biter': 15 / TESTING_THREAT_UNIT_DIVISOR,
  'medium-spitter': 50 / TESTING_THREAT_UNIT_DIVISOR,
  'medium-biter': 75 / TESTING_THREAT_UNIT_DIVISOR,
  'big-spitter': 200 / TESTING_THREAT_UNIT_DIVISOR,
  'big-biter': 375 / TESTING_THREAT_UNIT_DIVISOR,
  'behemoth-spitter': 1500 / TESTING_THREAT_UNIT_DIVISOR,
  'behemoth-biter': 3000 / TESTING_THREAT_UNIT_DIVISOR,
  'small-worm-turret': 200 / TESTING_THREAT_UNIT_DIVISOR,
  'medium-worm-turret': 500 / TESTING_THREAT_UNIT_DIVISOR,
  'big-worm-turret': 1500 / TESTING_THREAT_UNIT_DIVISOR,
  'behemoth-worm-turret': 3000 / TESTING_THREAT_UNIT_DIVISOR,
  'biter-spawner': 350 / TESTING_THREAT_UNIT_DIVISOR,
  'spitter-spawner': 350 / TESTING_THREAT_UNIT_DIVISOR,
};

/** The three numbers that replace the whole feeding loop — feeding_calculations.lua. */
export const TESTING_FEED = {
  /** `evo_scale` — mutagen that buys 100% evolution. */
  evoScale: 276.66,
  /** `evo_power` — E = (M / evo_scale) ^ evo_power. */
  evoPower: 0.3,
  /** `small_biters_per_food` — passive threat per second per unit of total mutagen. */
  smallBitersPerFood: 1.5,
};

/**
 * Combat modifiers the branch also moves. The lab charts none of them — they
 * touch player weapons, not the mutagen loop — but they are part of the same
 * balance change, so the branch panel reports them rather than pretending the
 * diff is only the curves.
 *
 *   `Public.base_ammo_modifiers`, `Public.base_turret_attack_modifiers` — tables.lua
 */
export interface CombatModifierChange {
  label: string;
  from: number;
  to: number;
}

export const TESTING_COMBAT_CHANGES: CombatModifierChange[] = [
  { label: 'flamethrower ammo', from: -0.6, to: -0.72 },
  { label: 'flamethrower turret', from: -0.8, to: -0.68 },
];

export const TIER_COLOURS = {
  small: '#8fbf6a',
  medium: '#e0a24a',
  big: '#d1585c',
  behemoth: '#9d6ad1',
} as const;
